/**
 *  2023.05.10 홍선영
 *  수정사항: 공백제거(trim) 필요 여부에 따라 밸류값 트림 따로 처리
 *
 * 2025.02.05 박승호
 *  수정사항: maxLength 초과 시 alert 띄우기 + 쓰로틀링 설정
 *
 */

import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { allowOneDecimalPointOnly, decimalNumberRegex, quoteRegex } from '../utils/checkRegex';
import { FLAG_CREATE_OR_UPDATE } from '../_constants';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { isIos } from '../utils/isIos';
import { isMobile } from '../utils/checkMobile';

interface Props {
  floatingPlaceholder: boolean | undefined;
  hasContent: boolean;
  ico?: string;
  clearable?: boolean;
}

const InputBox = styled.div<Props>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: ${(props) => (props.floatingPlaceholder || props.ico || props.clearable ? 'relative' : '')};

  > .ico {
    position: absolute;
    left: 10px;
    top: 0.4rem;
    color: #c6c6c6;
  }

  @media (max-width: 1024px) {
    input[type='text'] {
      padding: ${(props) => (props.ico ? '0 0.75rem 0 2.5rem' : '0 0.75rem')};
    }
    input[type='search'] {
      padding: ${(props) => (props.ico ? '0 0.75rem 0 2.5rem' : '0 0.75rem')};
    }
  }

  > .floatingPlaceholder {
    line-height: 20px;
    position: absolute;
    color: #c6c6c6;
    font-weight: normal;
    pointer-events: none;
    left: 10px;
    top: ${(props) => (!props.hasContent ? '0px' : '-10px')};
    font-size: ${(props) => (!props.hasContent ? '14px' : '12px')};
    color: ${(props) => (!props.hasContent ? '#c6c6c6' : '#328dd2')};
    transition: 300ms ease all;
    padding: 0.5rem 0;
  }

  input {
    height: ${(props) => (props.floatingPlaceholder ? '2.5rem !important' : undefined)};
    padding-top: ${(props) => (props.floatingPlaceholder ? '1rem' : '0')};
    padding-right: ${(props) => (props.clearable ? '2.25rem' : undefined)};
  }

  input:focus {
    outline: none;
  }

  input:focus ~ .floatingPlaceholder {
    top: -10px;
    font-size: 12px;
    color: #328dd2;
  }
  .clear-btn {
    position: absolute;
    top: 50%;
    right: 0.5rem;
    transform: translateY(-50%);
    border: none;
    background: transparent;
    padding: 0.125rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-disabled);

    span {
      font-size: 1rem;
    }

    &:hover {
      color: var(--text-primary);
    }
  }
  > label {
    white-space: break-spaces;
  }

  /* iOS에서 textarea 스타일 input과 동일하게 적용 */
  textarea {
    height: ${(props) => (props.floatingPlaceholder ? '2.5rem !important' : undefined)};
    padding-top: ${(props) => (props.floatingPlaceholder ? '1rem' : '0')};
    outline: none;
    border: none;
    border-radius: 6px;
    background-color: transparent;
    color: var(--text-primary);
    font-size: 0.875rem;
    font-weight: normal;
  }
`;

export const CheckBox = styled.div`
  display: flex;
  align-items: center;
  > label {
    user-select: none;
    /* font-size: 1.25rem; */
    /* font-weight: initial; */
  }
`;

interface InputProps {
  name: string;
  label?: string;
  type: string;
  state: any;
  setState: Dispatch<SetStateAction<any>>;
  placeholder?: string;
  floatingPlaceholder?: boolean;
  id?: string;
  blur?: () => void;
  className?: string;
  maxLength?: number;
  inputRef?: any;
  disabled?: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  trim?: boolean; // 공백제거 여부 (e.g. 아이디, 비밀번호 등은 공백제거 true)
  getBorderStyle?: any; // 선택값이 없을 때 보더 스타일
  stateType?: { type: 'array' | 'object'; index: number };
  valueType?: 'decimal' | string;
  useFlag?: boolean;
  onFocus?: () => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  ico?: string;
  canCopyToClipboard?: (contents: string) => void;
  iosTextArea?: boolean;
  clearable?: boolean;
  setIsUpdated?: (isUpdated: boolean) => void;
}

const mobile = isMobile();

const Input = ({
  name,
  label,
  type,
  state,
  setState,
  placeholder = '',
  floatingPlaceholder,
  id,
  blur,
  className,
  maxLength = 50,
  inputRef,
  disabled,
  onKeyDown,
  trim,
  getBorderStyle,
  stateType,
  valueType,
  useFlag,
  onFocus,
  onChange,
  onClick,
  ico,
  canCopyToClipboard,
  iosTextArea = false,
  clearable = false,
  setIsUpdated,
}: InputProps) => {
  const { t } = useTranslation();
  const [isThrottle, setIsThrottle] = useState<boolean>(false); // 쓰로틀링 플래그 설정

  const currentValue = stateType?.type === 'array' ? (Array.isArray(state) && state[stateType.index]?.[name]) || '' : state?.[name] || '';
  const hasContent = useMemo(() => {
    if (clearable) return true; // clearable이면 항상 true (항상 클리어 버튼 표시)
    return currentValue.length > 0;
  }, [clearable, currentValue]);

  const showClearButton = clearable && !mobile && type !== 'checkbox';
  const onChangeValue = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name: currentName, value } = e.target;
    const checkedValue = quoteRegex(value);
    let newValue = checkedValue;

    // userId 또는 userName인 경우 공백 입력 무시
    if (id === 'userId' || id === 'userName') {
      newValue = newValue.replace(/\s/g, '');
    }

    if (maxLength && checkedValue.length > maxLength) {
      if (!isThrottle) {
        toast.warning(`${label ? `${label} 필드는 ` : ''}최대 ${maxLength}자까지 입력 가능합니다.`); // maxLength 초과 시 alert 띄우기
        setIsThrottle(true);
        setTimeout(() => setIsThrottle(false), 2000); // alert은 2초에 1번만 실행되게 설정
      }
      newValue = checkedValue.slice(0, maxLength);
    }

    if (valueType === 'decimal') {
      newValue = decimalNumberRegex(newValue);
      newValue = allowOneDecimalPointOnly(newValue);
    }

    if (valueType === 'localeString') {
      newValue = value?.replace(/[^\d]/g, '');
      newValue = value ? (!isNaN(parseInt(newValue, 10)) ? parseInt(newValue, 10).toLocaleString() : newValue) : '0';
    }

    if (valueType === 'stringNumber') {
      newValue = value?.replace(/[^\d]/g, '');
    }

    if (currentName === 'address1') {
      setState((prev: any) => ({ ...prev, address1: '', postNum: '', latitude: '', longitude: '' }));
    }

    if (stateType?.type === 'array') {
      const copyState = [...state];
      copyState[stateType.index][e.target.name] = trim ? newValue.trim() : newValue;
      copyState[stateType.index] = useFlag ? { ...copyState[stateType.index], flag: FLAG_CREATE_OR_UPDATE } : { ...copyState[stateType.index] };
      setState(copyState);
    } else {
      setState((prev: any) => ({ ...prev, [e.target.name]: trim ? newValue?.trim() : newValue }));
    }
  };

  const handleClear = useCallback(() => {
    const element = inputRef?.current;
    if (element) {
      element.focus();
    }

    if (stateType?.type === 'array') {
      setState((prev: any) => {
        const newArray = [...prev];
        newArray[stateType.index] = { ...newArray[stateType.index], [name]: '' };
        return newArray;
      });
    } else {
      setState((prev: any) => ({ ...prev, [name]: '' }));
    }
  }, [stateType, name]);

  const onChangeCheckedValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState(e.target.checked);
  };

  return (
    <div>
      {type === 'checkbox' ? (
        <CheckBox>
          <input type={type} id={id} placeholder={placeholder} name={name} onChange={onChangeCheckedValue} checked={state} />
          <label htmlFor={id}>{label}</label>
        </CheckBox>
      ) : (
        <InputBox className={ico ? 'ico-input' : ''} floatingPlaceholder={floatingPlaceholder} ico={ico} clearable={clearable} hasContent={hasContent}>
          {!floatingPlaceholder && label && (
            <label
              htmlFor={`inputName-${name}`}
              className={className}
              role='presentation'
              onClick={
                canCopyToClipboard
                  ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const contents = stateType?.type !== 'array' ? state[name] || '' : state[stateType.index]?.[name] || '';
                      canCopyToClipboard(contents);
                    }
                  : undefined
              }
            >
              {t(label)}
            </label>
          )}
          {ico && <span className='material-symbols-rounded ico'>{ico}</span>}
          {isIos() && iosTextArea ? (
            <textarea
              placeholder={t(placeholder)}
              name={name}
              id={`inputName-${name}`}
              onChange={(e) => {
                onChange ? onChange(e as any) : onChangeValue(e as any);
                setIsUpdated?.(true);
              }}
              onBlur={blur}
              value={stateType?.type === 'array' ? state[stateType.index][name] || '' : state[name] || ''}
              // maxLength={maxLength}
              ref={inputRef}
              disabled={disabled}
              onKeyDown={onKeyDown ? (e) => onKeyDown?.(e as any) : undefined}
              style={getBorderStyle}
              onFocus={onFocus}
              onClick={onClick ? (e) => onClick?.(e as any) : undefined}
            />
          ) : (
            <input
              type={type}
              placeholder={t(placeholder)}
              name={name}
              id={`inputName-${name}`}
              onChange={(e) => {
                onChange ? onChange(e as any) : onChangeValue(e as any);
                setIsUpdated?.(true);
              }}
              onBlur={blur}
              value={stateType?.type === 'array' ? state[stateType.index][name] || '' : state[name] || ''}
              // maxLength={maxLength}
              ref={inputRef}
              disabled={disabled}
              onKeyDown={onKeyDown}
              style={getBorderStyle}
              onFocus={onFocus}
              onClick={onClick}
            />
          )}
          {showClearButton && (
            <button type='button' className='clear-btn' onClick={handleClear} aria-label={t('입력 삭제')}>
              <span className='material-symbols-rounded'>close</span>
            </button>
          )}
          {floatingPlaceholder && (
            <label htmlFor={`inputName-${name}`} className={className}>
              {label}
            </label>
          )}
        </InputBox>
      )}
    </div>
  );
};

export default Input;
